import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'
import { AssetFormData } from './index'

interface TourStep {
    selector: string
    title: string
    description: string
    fillDemoData?: boolean
}

interface UseCreateAssetTourProps {
    currentStep: number
    setFormData: React.Dispatch<React.SetStateAction<AssetFormData>>
}

export const useCreateAssetTour = ({
    currentStep,
    setFormData,
}: UseCreateAssetTourProps) => {
    const { t } = useTranslation('asset')
    const [isActive, setIsActive] = useState(false)

    const STEPS_STEP1: TourStep[] = useMemo(
        () => [
            {
                selector: '[data-tour="basic-info-form"]',
                title: t(
                    'tour.createAsset.step1.info.title',
                    'Thông tin cơ bản'
                ),
                description: t(
                    'tour.createAsset.step1.info.description',
                    'Nhập các thông tin bắt buộc như Tên tài liệu, Nhóm tài liệu, Loại tài liệu...'
                ),
                fillDemoData: true,
            },
            {
                selector: '[data-tour="next-button"]',
                title: t('tour.createAsset.step1.next.title', 'Tiếp tục'),
                description: t(
                    'tour.createAsset.step1.next.description',
                    'Nhấn Tiếp tục để lưu bản nháp và sang bước tiếp theo.'
                ),
            },
        ],
        [t]
    )

    const STEPS_STEP2: TourStep[] = useMemo(
        () => [
            {
                selector: '[data-tour="upload-area"]',
                title: t(
                    'tour.createAsset.step2.upload.title',
                    'Tải lên tài liệu'
                ),
                description: t(
                    'tour.createAsset.step2.upload.description',
                    'Kéo thả hoặc chọn file để tải lên. Hỗ trợ PDF, Word, Excel, Images...'
                ),
                fillDemoData: true,
            },
            {
                selector: '[data-tour="next-button"]',
                title: t('tour.createAsset.step2.next.title', 'Tiếp tục'),
                description: t(
                    'tour.createAsset.step2.next.description',
                    'Sau khi tải file lên, nhấn Tiếp tục để xem lại thông tin.'
                ),
            },
        ],
        [t]
    )

    const STEPS_STEP3: TourStep[] = useMemo(
        () => [
            {
                selector: '[data-tour="review-info"]',
                title: t(
                    'tour.createAsset.step3.review.title',
                    'Xem lại thông tin'
                ),
                description: t(
                    'tour.createAsset.step3.review.description',
                    'Kiểm tra lại toàn bộ thông tin tài liệu đã nhập trước khi gửi.'
                ),
            },
            {
                selector: '[data-tour="prev-button"]',
                title: t('tour.createAsset.step3.prev.title', 'Quay lại'),
                description: t(
                    'tour.createAsset.step3.prev.description',
                    'Có thể về step trước để chỉnh sửa thông tin hoặc tải lại file.'
                ),
            },
            {
                selector: '[data-tour="submit-button"]',
                title: t(
                    'tour.createAsset.step3.submit.title',
                    'Gửi phê duyệt'
                ),
                description: t(
                    'tour.createAsset.step3.submit.description',
                    'Gửi hồ sơ tài liệu để cấp trên phê duyệt.'
                ),
            },
        ],
        [t]
    )

    const getStepsForCurrentStep = useCallback(() => {
        switch (currentStep) {
            case 1:
                return STEPS_STEP1
            case 2:
                return STEPS_STEP2
            case 3:
                return STEPS_STEP3
            default:
                return []
        }
    }, [currentStep, STEPS_STEP1, STEPS_STEP2, STEPS_STEP3])

    const fillDemoData = useCallback(() => {
        if (currentStep === 1) {
            setFormData((prev) => ({
                ...prev,
                assetName: 'Demo Asset Documentation',
                assetDescription:
                    'This is a demo asset created by the product tour.',
                documentGroup: 'HR_CULTURE',
                documentType: 'POLICY',
                confidentialityLevel: 'INTERNAL',
                integrityLevel: 'MEDIUM',
                availabilityLevel: 'MEDIUM',
                departmentId: '1', // Assuming 1 exists or is valid enough for demo
                shareScope: 'public',
            }))
        } else if (currentStep === 2) {
            setFormData((prev) => ({
                ...prev,
                uploadedFiles: [
                    {
                        file_name: 'demo-policy-document.pdf',
                        file_path: 'temp/demo-policy-document.pdf',
                        file_size: 1024 * 1024 * 2.5, // 2.5 MB
                        file_type: 'application/pdf',
                        uploaded_at: new Date().toISOString(),
                    },
                ],
            }))
        }
    }, [currentStep, setFormData])

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
    }, [])

    // We need to break circular dependency showTourStep <-> goToStep
    // We can merge them or use a ref.
    // Let's just implement 'showStep' logic inside one function or use a recursive helper *outside* the hook or via ref.

    // Better approach: use a ref to hold the current 'showStep' function so we can call it.
    const showTourStepRef = useRef<(index: number, steps: TourStep[]) => void>(
        () => {}
    )

    const showTourStep = useCallback(
        (index: number, steps: TourStep[]) => {
            injectTourStyles()
            cleanupTourElements()
            createTourOverlay()

            const step = steps[index]
            if (!step) return

            if (step.fillDemoData) {
                fillDemoData()
            }

            const element = document.querySelector(step.selector) as HTMLElement
            if (!element) {
                if (index < steps.length - 1) {
                    // Recursive call via ref or simple recursion if we didn't use closure const
                    // We can just call showTourStepRef.current
                    showTourStepRef.current(index + 1, steps)
                } else {
                    endTour()
                }
                return
            }

            element.classList.add('tour-highlight-element')
            element.scrollIntoView({ behavior: 'smooth', block: 'center' })

            setTimeout(() => {
                const isLastStep = index === steps.length - 1
                const popover = createTourPopover({
                    title: step.title,
                    description: step.description,
                    currentStep: index + 1,
                    totalSteps: steps.length,
                    onPrev:
                        index > 0
                            ? () => showTourStepRef.current(index - 1, steps)
                            : undefined,
                    onNext: isLastStep
                        ? endTour
                        : () => showTourStepRef.current(index + 1, steps),
                    onClose: endTour,
                    isLastStep,
                })

                document.body.appendChild(popover)
                positionPopover(popover, element)
            }, 300)
        },
        [endTour, fillDemoData]
    )

    // Update ref
    useEffect(() => {
        showTourStepRef.current = showTourStep
    }, [showTourStep])

    const startTour = useCallback(() => {
        const steps = getStepsForCurrentStep()
        if (steps.length === 0) return

        setIsActive(true)

        // Wait for render
        setTimeout(() => {
            // Start at 0
            showTourStep(0, steps)
        }, 300)
    }, [getStepsForCurrentStep, showTourStep])

    // Restart tour when form step changes IF it was active
    useEffect(() => {
        if (isActive) {
            // Need to wait for new step DOM to be ready
            setTimeout(() => {
                const steps = getStepsForCurrentStep()
                if (steps.length > 0) {
                    showTourStep(0, steps)
                } else {
                    endTour()
                }
            }, 500)
        }
    }, [currentStep]) // Only depend on currentStep changing - wait, if isActive is true, we want to continue tour? yes.

    // Cleanup
    useEffect(() => {
        return () => cleanupTourElements()
    }, [])

    return {
        startTour,
        isActive,
    }
}
